import { CreationPanel, CustomDialog, EditionPanel, InfiniteScroll } from '@features/common';
import SightLoading from '@features/common/Loading/SightLoading';
import { Cities } from '@features/customers/interfaces/search.interfaces';
import DivisionDetails from '@features/divisions/components/DivisionDetails';
import DivisionEditionForm from '@features/divisions/components/DivisionEditionForm';
import { handleSaveDivisionEditionData } from '@features/divisions/components/DivisionEditionForm/DivisionEditionForm.handlers';
import { DivisionEditionFormType } from '@features/divisions/interfaces/division-edition.interface';
import {
	FiltersStates,
	InputSelectOptions,
	ProcessesFiltersForm,
	TitleHeaderContainer,
} from '@features/filters';
import { AddProcessForm, ProcessesContainer } from '@features/processes';
import { handleSaveProcessCreationData } from '@features/processes/components/AddProcessForm/AddProcessForm.handlers';
import ProcessEditionForm from '@features/processes/components/ProcessEditionForm';
import {
	handleSaveProcessData,
	handleSaveProcessEditionData,
} from '@features/processes/components/ProcessEditionForm/ProcessEditionForm.handlers';
import { DEFAULT_PROCESS_CREATION_DATA } from '@features/processes/constants/process-forms.constants';
import useProcessesContainerStyles from '@features/processes/containers/ProcessesContainer/ProcessesContainer.styles';
import ProcessCreationFormType from '@features/processes/interfaces/process-creation.interface';
import { ProcessEditionFormType } from '@features/processes/interfaces/process-edition.interface';
import useRequests from '@hooks/requests.hook';
import {
	ClienteSuite,
	DivisionCliente,
	ProcesoDivision,
} from '@interfaces/firestore-documents.interfaces';
import { Search } from '@mui/icons-material';
import { Typography } from '@mui/material';
import clsx from 'clsx';
import { useSnackbar } from 'notistack';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
	handleBackButtonClick,
	handleBackBeforeCloseDialog,
	handleCancelEditionPanel,
	handleDivisionDetailsClick,
	handleProcessClick,
	handleClosePanel,
	handleBackBeforeSaveDialog,
	handleAddProcessClick,
	searchProcesses,
	handleFiltersClearClick,
	handleFiltersKeyPress,
} from './SingleDivisionContainer.handlers';
import useSingleDivisionContainerStyles from './SingleDivisionContainer.styles';
import { FormType } from './SingleDivisionContainer.types';

interface Props {
	data: DivisionCliente;
	cities: Cities[];
	nit: string;
	setDivisionNumber: React.Dispatch<React.SetStateAction<string>>;
	getDataById: (identificacion: string) => Promise<void>;
	allData: ClienteSuite | null;
}

const SingleDivisionContainer: React.FC<Props> = ({
	data,
	nit,
	setDivisionNumber,
	cities,
	allData,
	getDataById,
}) => {
	// States
	const [show, setShow] = useState(false);
	const [afterSaveOnBeforeCloseDialogAction, setAfterSaveOnBeforeCloseDialogAction] = useState<
		(() => void) | null
	>(null);

	const [editionProcessData, setEditionProcessData] = useState<ProcesoDivision | null>(null);
	const [beforeSaveData, setBeforeSaveData] = useState<ProcessEditionFormType | null>(null);
	const [beforeCloseDialog, setBeforeCloseDialog] = useState<boolean>(false);
	const [form, setForm] = useState<FormType>('none');
	const [beforeSaveDialog, setBeforeSaveDialog] = useState<string>('');
	const [processes, setProcesses] = useState<ProcesoDivision[]>([]);
	const classesProcess = useProcessesContainerStyles();
	const [scrollPage, setScrollPage] = useState<number>(10);

	// Variables
	const rangosCliente = allData?.rangos_cliente !== undefined ? allData.rangos_cliente : [];
	const rangeActual = data.agw_ranges.find((range) => range.en_uso);

	const defaultDivisionEditionValues: DivisionEditionFormType = {
		agw_ranges: data.agw_ranges.map((range) => ({ ...range })),
		combos: data.combos.map((c) => c.id_combo).sort((a, b) => a - b),
		consecutivo_desde: rangeActual?.consecutivo_desde,
		consecutivo_hasta: rangeActual?.consecutivo_hasta,
		notificacion_remitente: data.notificacion_remitente,
		prefijo_consecutivo: rangeActual?.prefijo_consecutivo,
		en_uso: rangeActual?.en_uso || false,
		habilitado_retencion_guia: data.habilitado_retencion_guia,
	};

	const processData = data.procesos.sort((a, b) => a.id_proceso - b.id_proceso);
	const processesContainer = useRef<HTMLDivElement | null>(null);

	// Hooks
	const classes = useSingleDivisionContainerStyles();
	const { enqueueSnackbar } = useSnackbar();
	const { requestBackendSuite, request, requestAdministraCliente } = useRequests();

	const processCreationForm = useForm<ProcessCreationFormType>({
		defaultValues: DEFAULT_PROCESS_CREATION_DATA,
	});

	const processEditionForm = useForm<ProcessEditionFormType>();

	const divisionEditionForm = useForm<DivisionEditionFormType>({
		defaultValues: defaultDivisionEditionValues,
	});

	const filtersForm = useForm<ProcessesFiltersForm>({
		defaultValues: {
			process: '',
			state: FiltersStates.All,
		},
	});

	const onCloseHandle = handleClosePanel(
		data,
		divisionEditionForm,
		form,
		processCreationForm,
		processEditionForm,
		setAfterSaveOnBeforeCloseDialogAction,
		setBeforeCloseDialog,
		setBeforeSaveData,
		setBeforeSaveDialog,
		setEditionProcessData,
		setForm
	);

	const onSaveHandle =
		form === 'edite-division'
			? handleSaveDivisionEditionData(
					afterSaveOnBeforeCloseDialogAction,
					data,
					divisionEditionForm,
					enqueueSnackbar,
					nit,
					processCreationForm,
					processEditionForm,
					request,
					requestAdministraCliente,
					setAfterSaveOnBeforeCloseDialogAction,
					setBeforeCloseDialog,
					setBeforeSaveData,
					setBeforeSaveDialog,
					setEditionProcessData,
					setForm,
					setShow,
					getDataById
			  )
			: handleSaveProcessEditionData(
					afterSaveOnBeforeCloseDialogAction,
					editionProcessData,
					data,
					divisionEditionForm,
					enqueueSnackbar,
					nit,
					processCreationForm,
					processEditionForm,
					request,
					requestBackendSuite,
					setAfterSaveOnBeforeCloseDialogAction,
					setBeforeCloseDialog,
					setBeforeSaveData,
					setBeforeSaveDialog,
					setEditionProcessData,
					setForm,
					setShow,
					getDataById
			  );

	// Variables
	const process = filtersForm.watch('process');
	const state = filtersForm.watch('state');

	const filtersValues: ProcessesFiltersForm = { process, state };

	// Effects
	useEffect(() => {
		divisionEditionForm.reset(defaultDivisionEditionValues);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, divisionEditionForm]);

	useEffect(() => {
		searchProcesses(processData, filtersValues, setProcesses);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [processData, state]);

	useEffect(() => {
		if (editionProcessData) {
			processEditionForm.reset({
				...editionProcessData,
				nombre_proceso: '',
				ranges: editionProcessData.ranges.map((r) => ({
					...r,
					consecutivo_actual: r.consecutivo_actual ?? r.consecutivo_desde,
				})),
			});
		}
	}, [editionProcessData, processEditionForm]);
	// const divisionInformationDetails = document.getElementById('division-information-details');
	// const processTitle = document.getElementById('division-information-processes-title');
	// const resultsHeight = `calc(100vh - ${SEARCH_CONTAINER_HEIGHT} - 31px - ${
	// 	divisionInformationDetails?.offsetHeight ?? 0
	// }px - ${processTitle?.offsetHeight ?? 0}px - 16px - 24px)`;
	const resultsHeight = `80vh`;

	return (
		<>
			<SightLoading show={show} />
			<div className={classes.root}>
				<div
					className={clsx(classes.container, {
						[classes.containerEditing]: form !== 'none' && form !== 'create-process',
					})}
				>
					<TitleHeaderContainer
						rangosCliente={rangosCliente}
						addButton={{
							onClick: handleAddProcessClick(
								divisionEditionForm,
								form,
								processEditionForm,
								setAfterSaveOnBeforeCloseDialogAction,
								setBeforeCloseDialog,
								setForm
							),
							tooltip: 'Crear nuevo proceso',
						}}
						backButton={{
							onClick: handleBackButtonClick(
								divisionEditionForm,
								form,
								processEditionForm,
								setAfterSaveOnBeforeCloseDialogAction,
								setBeforeCloseDialog,
								setDivisionNumber
							),
						}}
						className={classes.titleContainer}
						counter={Object.keys(filtersForm.formState.dirtyFields).length ?? 0}
						id="division-information-processes-title"
						inputs={[
							{
								control: filtersForm.control,
								defaultValue: '',
								name: 'process',
								onClear: handleFiltersClearClick(
									processData,
									filtersValues,
									setProcesses
								),
								onKeyPress: handleFiltersKeyPress(
									processData,
									filtersValues,
									setProcesses
								),
								onChange: () => {
									return async (e) => {
										if (e) {
											filtersForm.setValue('process', e.target.value);
											if (e.target.value === '') {
												searchProcesses(
													processData,
													filtersForm.getValues(),
													setProcesses
												);
											}
										} else {
											filtersForm.setValue('process', '');
										}
									};
								},
								placeholder: 'Escribe el proceso',
								startAdorment: <Search fontSize="small" />,
							},
							{
								control: filtersForm.control,
								defaultValue: FiltersStates.All,
								name: 'state',
								options: Object.values(FiltersStates).map((value) => ({
									value,
									label: value.toString(),
								})),
								width: 211,
							} as InputSelectOptions<ProcessesFiltersForm, FiltersStates>,
						]}
						title={
							<>
								<Typography
									color="#686868"
									component="span"
									fontFamily="Montserrat"
									fontSize={18}
									fontWeight="bold"
									id="division-information-details-title"
								>
									División {data.codigo_div}{' '}
								</Typography>
								/ Procesos
							</>
						}
					/>
					<div
						className={classesProcess.root}
						ref={processesContainer}
						style={{ maxHeight: resultsHeight }}
					>
						<InfiniteScroll
							className={classesProcess.scrollContainer}
							dataLength={processes.length}
							lengthPerPage={9}
							loadMore={setScrollPage}
							parentElement={processesContainer}
						>
							<DivisionDetails
								active={form === 'edite-division'}
								data={data}
								onClick={handleDivisionDetailsClick(
									form,
									processEditionForm,
									setAfterSaveOnBeforeCloseDialogAction,
									setBeforeCloseDialog,
									setEditionProcessData,
									setForm
								)}
							/>
							{processData.length > 0 && (
								<ProcessesContainer
									editingProcessID={editionProcessData?.id_proceso}
									isEditing={form !== 'none' && form !== 'create-process'}
									onProcessEditionClick={handleProcessClick(
										divisionEditionForm,
										editionProcessData,
										form,
										processEditionForm,
										setAfterSaveOnBeforeCloseDialogAction,
										setBeforeCloseDialog,
										setEditionProcessData,
										setForm
									)}
									processes={processes}
									scrollPage={scrollPage}
									resultsHeight={resultsHeight}
								/>
							)}
						</InfiniteScroll>
					</div>
				</div>
			</div>
			{form === 'create-process' && (
				<CreationPanel
					width={714}
					onClose={onCloseHandle}
					onSave={handleSaveProcessCreationData(
						data,
						divisionEditionForm,
						enqueueSnackbar,
						nit,
						processCreationForm,
						processEditionForm,
						requestBackendSuite,
						setAfterSaveOnBeforeCloseDialogAction,
						setBeforeCloseDialog,
						setBeforeSaveData,
						setBeforeSaveDialog,
						setEditionProcessData,
						setForm,
						setShow,
						getDataById
					)}
					saveDisabled={
						processCreationForm.formState.isSubmitting ||
						processCreationForm.formState.isValidating ||
						Object.keys(processCreationForm.formState.dirtyFields).length === 0
					}
					title="Crear nuevo proceso"
				>
					<AddProcessForm
						allData={allData}
						cities={cities}
						division={data}
						form={processCreationForm}
					/>
				</CreationPanel>
			)}
			{form !== 'none' && form !== 'create-process' && (
				<EditionPanel
					bodyStyle={form === 'edite-division' ? undefined : { paddingTop: 24 }}
					loading={
						(form === 'edite-division' &&
							(divisionEditionForm.formState.isValidating ||
								divisionEditionForm.formState.isSubmitting)) ||
						(form === 'edite-process' &&
							(processEditionForm.formState.isValidating ||
								processEditionForm.formState.isSubmitting))
					}
					onCancel={handleCancelEditionPanel(
						null,
						data,
						divisionEditionForm,
						processCreationForm,
						processEditionForm,
						setAfterSaveOnBeforeCloseDialogAction,
						setBeforeCloseDialog,
						setBeforeSaveData,
						setBeforeSaveDialog,
						setEditionProcessData,
						setForm
					)}
					onClose={onCloseHandle}
					onSave={onSaveHandle}
					showActions={
						(form === 'edite-division' && divisionEditionForm.formState.isDirty) ||
						(form === 'edite-process' && processEditionForm.formState.isDirty)
					}
					subTitle={
						form === 'edite-division'
							? `(División: ${data.codigo_div})`
							: `(ID: ${editionProcessData?.id_proceso_text})`
					}
					title={
						form === 'edite-division'
							? data.nombre_div
							: editionProcessData?.nombre_proceso ?? ''
					}
				>
					{form === 'edite-division' ? (
						<DivisionEditionForm
							form={divisionEditionForm}
							cities={cities}
							allData={allData}
							getDataById={getDataById}
							codigoDiv={data.codigo_div}
							nit={nit}
							data={data}
							nivelesServicio={data.niveles_servicio}
						/>
					) : (
						<ProcessEditionForm
							defaultState={editionProcessData?.estado ?? true}
							cities={cities}
							division={data}
							allData={allData}
							getDataById={getDataById}
							form={processEditionForm}
							id_proceso={editionProcessData?.id_proceso_text || ''}
						/>
					)}
				</EditionPanel>
			)}
			{beforeCloseDialog && (
				<CustomDialog
					body={
						form !== 'create-process' ? (
							<>
								Hay <strong>cambios sin guardar</strong> que se borrarán
								<br />
								¿Deseas cerrar sin guardar?
							</>
						) : undefined
					}
					leftAction="CERRAR"
					onLeftAction={handleCancelEditionPanel(
						afterSaveOnBeforeCloseDialogAction,
						data,
						divisionEditionForm,
						processCreationForm,
						processEditionForm,
						setAfterSaveOnBeforeCloseDialogAction,
						setBeforeCloseDialog,
						setBeforeSaveData,
						setBeforeSaveDialog,
						setEditionProcessData,
						setForm
					)}
					onRightAction={handleBackBeforeCloseDialog(setBeforeCloseDialog)}
					rightAction="SEGUIR EDITANDO"
					title={
						form === 'create-process'
							? '¿Desea cerrar el formulario?'
							: 'No se han guardado los cambios'
					}
				/>
			)}
			{beforeSaveDialog.length > 0 && editionProcessData && beforeSaveData && (
				<CustomDialog
					body={
						<>
							Hiciste <strong>un cambio</strong> en{' '}
							{beforeSaveDialog !== 'all' ? (
								<>
									el <strong>{beforeSaveDialog}</strong>
								</>
							) : null}{' '}
							Rango de guías y Consecutivo
						</>
					}
					leftAction="GUARDAR"
					onLeftAction={handleSaveProcessData(
						null,
						editionProcessData,
						data,
						divisionEditionForm,
						enqueueSnackbar,
						beforeSaveData,
						nit,
						processCreationForm,
						processEditionForm,
						request,
						requestBackendSuite,
						setAfterSaveOnBeforeCloseDialogAction,
						setBeforeCloseDialog,
						setBeforeSaveData,
						setBeforeSaveDialog,
						setEditionProcessData,
						setForm,
						setShow,
						getDataById
					)}
					onRightAction={handleBackBeforeSaveDialog(
						setBeforeSaveData,
						setBeforeSaveDialog
					)}
					rightAction="CANCELAR"
					title="¿Deseas guardar los cambios?"
				/>
			)}
		</>
	);
};

export default SingleDivisionContainer;
